import type {
  HelpWarningOutput,
  PayRiseOutput,
  SecondJobOutput,
  TakeHomeOutput,
  WithholdingGapOutput,
} from "./types.js";

// Plain-English result copy for the take-home net-pay modes. Centralised in the
// rules package so every surface (Expo result screens, "my results", PDF
// reports) renders identical wording from one source — which matters for
// regulation, not just DRY. Every string here is INFORMATION, not advice
// (validation §3.1): it states what the figure IS and how it derives from the
// ATO schedules, with NO "save $X", NO "you should", NO product/credit handoff,
// NO "ask for a bigger raise". Each personalised result is an ESTIMATE and says
// so — the ATO finalises the actual assessment. Drift between channels is the
// information→advice kill-threat, so the copy lives here beside the rules.

/** Whole-dollar AUD with thousands separators. Local to the copy layer so the
 * wording renders the same numbers everywhere, independent of any app's UI. */
export function formatAud(amount: number): string {
  return `A$${Math.round(amount).toLocaleString("en-AU")}`;
}

/** Render a fraction as a whole-or-1dp percentage (e.g. 0.53 → "53%"). */
function pct(fraction: number): string {
  return `${+(fraction * 100).toFixed(1)}%`;
}

/** The mandatory information-not-advice disclaimer, identical on every surface. */
export const DISCLAIMER =
  "Educational tool. Not financial advice. Figures are estimates based on " +
  "the ATO schedules in force for the financial year shown; the ATO finalises " +
  "your actual assessment when you lodge.";

export function takeHomeHeadline(out: TakeHomeOutput): string {
  return `Your estimated take-home pay for the year: ${formatAud(out.takeHome)}`;
}

export function takeHomeBullet(out: TakeHomeOutput): string {
  const help =
    out.helpRepayment > 0
      ? ` and a compulsory study loan repayment of ${formatAud(out.helpRepayment)}`
      : "";
  return `On a gross income of ${formatAud(out.grossIncome)}, your estimated income tax is ${formatAud(out.incomeTax)}, your Medicare levy is ${formatAud(out.medicareLevy)}${help}.`;
}

/** Pay-rise: states the keep-rate as a figure — never as a nudge to ask for more. */
export function payRiseHeadline(out: PayRiseOutput): string {
  return `Of a ${formatAud(out.riseAmount)} pay rise, you would keep an estimated ${formatAud(out.keptAmount)} — a keep-rate of ${pct(out.keepRate)}.`;
}

export function payRiseBullet(out: PayRiseOutput): string {
  const parts = [
    `${formatAud(out.extraIncomeTax)} income tax`,
    `${formatAud(out.extraMedicareLevy)} Medicare levy`,
  ];
  if (out.litoClawback > 0) {
    parts.push(`${formatAud(out.litoClawback)} reduced low income tax offset`);
  }
  if (out.extraHelpRepayment > 0) {
    parts.push(
      `${formatAud(out.extraHelpRepayment)} extra compulsory study loan repayment`,
    );
  }
  return `The rise is reduced by ${parts.join(", ")} — ${formatAud(out.totalDeductions)} in total.`;
}

/** HELP silent-bill warning: states the predicted lump sum, descriptive only. */
export function helpWarningHeadline(out: HelpWarningOutput): string {
  if (!out.silentBill) {
    return out.employerWithholdsStsl
      ? `Your employer is withholding for your study loan, so no lump-sum study loan bill is expected at lodgement.`
      : `No compulsory study loan repayment applies at this repayment income.`;
  }
  return `Because your study loan is not being withheld, an estimated ${formatAud(out.predictedLumpSumBill)} compulsory repayment is likely to fall due as a lump sum when you lodge.`;
}

export function helpWarningBullet(out: HelpWarningOutput): string {
  return `Your compulsory study loan repayment on a repayment income of ${formatAud(out.repaymentIncome)} is ${formatAud(out.compulsoryRepayment)} for the year. Study loan withholding is opt-in (the question on your TFN or withholding declaration); if it is not switched on, nothing is withheld through the year.`;
}

/** Withholding-gap: states the predicted refund or bill, descriptive only. */
export function withholdingGapHeadline(out: WithholdingGapOutput): string {
  if (out.outcome === "refund") {
    return `Your estimated annual liability is ${formatAud(out.annualLiability)} against ${formatAud(out.totalWithheld)} withheld — a predicted refund of ${formatAud(out.predictedRefund)}.`;
  }
  if (out.outcome === "bill") {
    return `Your estimated annual liability is ${formatAud(out.annualLiability)} against ${formatAud(out.totalWithheld)} withheld — a predicted bill of ${formatAud(out.predictedBill)}.`;
  }
  return `Your withholding of ${formatAud(out.totalWithheld)} matches your estimated annual liability of ${formatAud(out.annualLiability)}.`;
}

/** Second-job: states the predicted shortfall + the ATO's own fix lever. */
export function secondJobHeadline(out: SecondJobOutput): string {
  if (out.doubleCountedThreshold <= 0 || out.predictedShortfall <= 0) {
    return `No tax-free-threshold double-count is predicted across your jobs.`;
  }
  return `Claiming the tax-free threshold on both jobs leaves about ${formatAud(out.doubleCountedThreshold)} under-taxed at the margin — an estimated ${formatAud(out.predictedShortfall)} shortfall at lodgement.`;
}

export function secondJobBullet(out: SecondJobOutput): string {
  return `The ATO rule is to claim the tax-free threshold from only one payer (usually the higher-paying job). At a combined income of ${formatAud(out.combinedIncome)} your marginal rate is ${pct(out.marginalRate)}; the second payer treats the second job's first ${formatAud(out.doubleCountedThreshold)} as tax-free, which the threshold has already covered. You can ask a payer to withhold extra via a Withholding declaration (NAT 3093).`;
}

/** Source-attribution line, composed from ruleset metadata in force. */
export function sourceBullet(ruleVersion: string, verifiedAt: string): string {
  return `These figures use the ATO rates and thresholds for the ${ruleVersion} financial year, last verified ${verifiedAt}. Rates and thresholds are indexed each year — always check ato.gov.au.`;
}
